import { useEffect, useState } from "react";
import { Send, Mail, Calendar, Loader2, BarChart3, ChevronDown, ChevronUp, History } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin-only Compliance Pulse panel. Lives inside SecurityAdmin.
 *  • Preview the current month's digest (read-only)
 *  • Send to self (test) or to all admins
 *  • View send history
 */
export default function CompliancePulsePanel() {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingSelf, setSendingSelf] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await api.get("/security-admin/compliance-pulse/preview", { params: { window_days: 30 } });
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load pulse preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get("/security-admin/compliance-pulse/history", { params: { limit: 12 } });
      setHistory(r.data.items || []);
    } catch (_) {}
  };

  useEffect(() => { loadPreview(); loadHistory(); }, []);

  const sendToSelf = async () => {
    setSendingSelf(true);
    try {
      const r = await api.post("/security-admin/compliance-pulse/send", null, {
        params: { window_days: 30, to_self_only: true },
      });
      toast.success(r.data.sent_to?.length
        ? `Pulse queued to ${r.data.sent_to[0]}`
        : "Pulse generated (no email recipient)");
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send pulse");
    } finally {
      setSendingSelf(false);
    }
  };

  const sendToAllAdmins = async () => {
    if (!window.confirm("Send the compliance pulse to ALL admins now?")) return;
    setSendingAll(true);
    try {
      const r = await api.post("/security-admin/compliance-pulse/send", null, {
        params: { window_days: 30, to_self_only: false },
      });
      toast.success(`Pulse sent to ${r.data.sent_to?.length || 0} admin(s)`);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send pulse");
    } finally {
      setSendingAll(false);
    }
  };

  return (
    <section
      data-testid="compliance-pulse-panel"
      className="mb-10 bg-[var(--pa-surface)] border border-[var(--pa-gold)]/40 rounded-lg p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="kicker">Compliance · Monthly digest</div>
          <h2 className="font-serif text-xl text-[var(--pa-text)] mt-1">Compliance Pulse</h2>
          <p className="text-xs text-[var(--pa-text-muted)] mt-1 max-w-2xl">
            Aggregated 30-day digest covering disclaimer + BAA acknowledgments, 2FA enrollment, lockouts, and PHI access.
            Auto-emails to all admins on the 1st of each month (09:00 UTC).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            onClick={sendToSelf}
            disabled={sendingSelf || sendingAll}
            data-testid="compliance-send-self"
            className="inline-flex items-center gap-1.5 text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-[var(--pa-text)] rounded-md px-3 py-1.5 hover:border-[var(--pa-gold)]/60 transition disabled:opacity-50"
          >
            {sendingSelf ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
            Email me a test
          </button>
          <button
            onClick={sendToAllAdmins}
            disabled={sendingSelf || sendingAll}
            data-testid="compliance-send-all"
            className="inline-flex items-center gap-1.5 text-xs bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-3 py-1.5 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50"
          >
            {sendingAll ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Send to all admins
          </button>
        </div>
      </div>

      {loadingPreview && !preview && (
        <div className="text-xs text-[var(--pa-text-muted)] flex items-center gap-2" data-testid="compliance-preview-loading">
          <Loader2 size={12} className="animate-spin" /> Loading current pulse…
        </div>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="compliance-preview-stats">
            <MiniStat label="2FA enrollment" value={`${preview.users.totp_enrollment_pct}%`} sub={`${preview.users.totp_enrolled} / ${preview.users.total}`} testid="compliance-stat-2fa" />
            <MiniStat label="Admin 2FA" value={`${preview.users.admin_totp_enrollment_pct}%`} sub={`${preview.users.admin_totp_enrolled} / ${preview.users.admins_total}`} testid="compliance-stat-admin-2fa" />
            <MiniStat label="Disclaimer drift" value={preview.disclaimer.acked_stale_version} sub={`${preview.disclaimer.never_acked} never acked`} tone={preview.disclaimer.never_acked > 0 ? "text-amber-300" : null} testid="compliance-stat-disclaimer" />
            <MiniStat label="BAA drift" value={preview.baa.acked_stale_version} sub={`${preview.baa.never_acked} never acked`} tone={preview.baa.never_acked > 0 ? "text-amber-300" : null} testid="compliance-stat-baa" />
            <MiniStat label="Failed logins (30d)" value={preview.activity_window.failed_logins} testid="compliance-stat-failed" />
            <MiniStat label="Lockouts (30d)" value={preview.activity_window.lockouts} testid="compliance-stat-lockouts" />
            <MiniStat label="PHI events (30d)" value={preview.activity_window.phi_access_events} testid="compliance-stat-phi" />
            <MiniStat label="Audit total (30d)" value={preview.activity_window.audit_events_total} testid="compliance-stat-audit" />
          </div>

          <div className="space-y-2" data-testid="compliance-alerts">
            {preview.alerts.map((a, i) => (
              <div
                key={i}
                data-testid={`compliance-alert-${a.level}`}
                className={`p-3 rounded-md border text-xs ${
                  a.level === "critical"
                    ? "bg-red-500/10 border-red-500/40 text-red-300"
                    : a.level === "warning"
                    ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                    : "bg-sky-500/10 border-sky-500/40 text-sky-300"
                }`}
              >
                <div className="font-semibold">{a.title}</div>
                <div className="opacity-80 mt-0.5">{a.detail}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* History */}
      <div className="mt-5 border-t border-[var(--pa-border)] pt-4">
        <button
          onClick={() => setShowHistory((x) => !x)}
          data-testid="compliance-toggle-history"
          className="w-full flex items-center justify-between text-sm text-[var(--pa-text)] hover:text-[var(--pa-gold)] transition"
        >
          <span className="flex items-center gap-2">
            <History size={14} className="text-[var(--pa-gold)]" />
            Send history ({history.length})
          </span>
          {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showHistory && (
          history.length === 0 ? (
            <div className="text-[11px] text-[var(--pa-text-dim)] mt-3" data-testid="compliance-history-empty">
              No pulses sent yet. Use "Email me a test" to fire your first one.
            </div>
          ) : (
            <ul className="mt-3 space-y-1.5" data-testid="compliance-history-list">
              {history.map((row, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-2 text-xs p-2 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md"
                >
                  <div className="flex items-center gap-2 text-[var(--pa-text-muted)]">
                    <Calendar size={11} className="text-[var(--pa-gold)]" />
                    {new Date(row.generated_at).toLocaleString()}
                  </div>
                  <div className="text-[var(--pa-text-dim)]">
                    {row.window_days}d · {row.recipients?.length || 0} recipient{(row.recipients?.length || 0) === 1 ? "" : "s"}
                  </div>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </section>
  );
}

function MiniStat({ label, value, sub, testid, tone }) {
  return (
    <div data-testid={testid} className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim)]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tone || "text-[var(--pa-text)]"}`}>{value}</div>
      {sub && <div className="text-[10px] text-[var(--pa-text-dim)] mt-1">{sub}</div>}
    </div>
  );
}
